import { createGlobalStyle, css, keyframes } from 'styled-components'

const root = css`
    :root {
        --font-logo: Belgro;
        --font-open-sans: "Open Sans", sans-serif;
        --font-montserrat: "Montserrat", sans-serif;
        --transition: all 0.4s ease;
        --font-primary: 'Inter', sans-serif;
        --font-secondary: 'Istok Web', sans-serif;
        --font-tertiary: 'Open Sans', sans-serif;
        --gray: #ACACAC;
        --gray-bg: #F4F6F8;
        --gray-dark: #737070;
        --gray-dark-2: #464544;
        --gray-dark-3: #6D6D6D;
        --gray-darker: #5C5A5A;
        --gray-darker-2: #5C5959;
        --gray-light: #D9D9D9;
        --gray-light-1: #B9B9B9;
        --gray-light-2: #E6E6E7;
        --gray-light-3: #EFEFEF;
        --gray-light-4: #C2BBBB;
        --gray-lighter: #8C8C8C;
        --gray-lighter-2: #ACACAC;
        --gray-lighter-3: #F9F9F9;
        --gray-lighter-4: #f0f0f0;
        --gray-lighter-5: #B5B4B4;
        --gray-lighter-6: #B5B2B2;
        --white: #FDFDFD;
        --white-light: #FFFFFF;
        --white-lighter: #FAFAFA;
        --blue: #285FF5;
        --blue-darker: #044DB5;
        --blue-light: #5692E7;
        --aqua: #13D9A9;
        --black: #1B1B1A;
        --black-2: #1F1F1F;
        --black-3: #1D1D1D;
        --black-lighter: #1E1C1B;
        --black-dark: #000000;
        --red: #F52828;
        --orange: #F79200;
        --beige: #fcf1d466;
        --green: #1DB504;
        --green-dark: #1E9600;
        --green-light: #04B555;
    }
`

const rootColors = { default: root }

const GlobalStyle = createGlobalStyle`
    ${rootColors.default}

    *, ::after, :before { 
        margin: 0;
        padding: 0;
        border: none;
        outline: none;
        box-sizing: border-box;
        font-family: var(--font-primary)
    }

    *, ::before, ::after {
        box-sizing: border-box;
    }

    html {
        scroll-behavior: smooth;
    }

    body {
        margin: 0;
        padding: 0;
        font-size: 1rem;
    }

    button {
        cursor: pointer;
    }

    #root {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 100vh;
        overflow: hidden;
        font-family: var(--font-primary);
        font-style: normal;
    }

    .material-symbols-outlined {
        line-height: unset;
        font-variation-settings:
        'FILL' 0,
        'wght' 400,
        'GRAD' 0,
        'opsz' 48;
    }

    .ant-menu-item-icon {
        font-size: 1.5rem;
    }

    .ant-form-item-label {
        max-width: 100%;
        flex: 1;
    }

    .ant-form-item-control {
        max-width: 100%;
        flex: 1;
    }

    .ant-input-number {
        width: 100%;
    }

    .ant-form-item-label >label::after {
        display: none;
    }

    .ant-table-thead tr th {
        background-color: var(--blue-darker) !important;
        color: var(--white-light) !important;
        font-weight: 600 !important;
        font-size: 0.875rem !important;
        text-align: center !important;
    }

    .ant-btn-color-primary.ant-btn-variant-solid {
        background: var(--blue-darker);

        &:disabled {
            cursor: not-allowed;
            border-color: #d9d9d9;
            color: rgba(0, 0, 0, 0.25);
            background: rgba(0, 0, 0, 0.04);
            box-shadow: none;
        }
    }

    .ant-btn-color-primary.ant-btn-variant-outlined {
        border-color: var(--blue-darker);
        color: var(--blue-darker);

        &:disabled {
            cursor: not-allowed;
            border-color: #d9d9d9;
            color: rgba(0, 0, 0, 0.25);
            background: rgba(0, 0, 0, 0.04);
            box-shadow: none;
        }
    }

    button.secondary {
        background: var(--gray);
        color: var(#fff);
        border-color: var(--gray);

        &:hover {
            border-color: var(--gray) !important;
            opacity: 0.7;
        }
    }

    .ant-tag {
        font-family: var(--font-primary);
    }

    .ant-float-btn {
        width: 45px;
        height: 45px;

        .material-symbols-outlined {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.35rem;
        }
    }

    .ant-float-btn-primary {
        background-color: var(--blue-darker);

        .ant-float-btn-body {
            background-color: var(--blue-darker);
            padding-left: 1px;
        }
    }

    .ant-drawer {
        z-index: 101;

        .ant-drawer-title {
            @media (max-width: 760px) {
                display: none !important;
            }
        }
    }

    .ant-modal-content {
        max-height: 85dvh;
        overflow-y: auto;
    }

    .ant-descriptions {
        margin-bottom: 32px;
    }

    .ant-descriptions-item-content {
        text-align: center;
    }

    .ant-descriptions-item-label span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        font-weight: 600;
    }

    /* Fix Steps component number vertical alignment */
    .ant-steps-item-icon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .ant-steps-icon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
    }

    .ant-layout-header {
        padding: 12px 16px !important;
    }

    .sac-chatbot-drawer {
        @media (max-width: 760px) {
            .ant-drawer-content-wrapper {
                width: 100% !important;
                max-width: 100vw !important;
                height: 85vh !important;
            }
            
            .ant-drawer-body {
                height: calc(85vh - 55px) !important;
            }
        }
    }
`
export const fadeEaseIn = keyframes`
 0% {
    opacity: 0;
    visibility: hidden;
 }
 100% {
    opacity: 1;
    visibility: visible;
 }
`

export const tableEnter = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.99); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`

export const tabContentEnter = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

export const pillEnter = keyframes`
  from { opacity: 0; transform: scaleY(0.92); }
  to   { opacity: 1; transform: scaleY(1); }
`

export const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`

export default GlobalStyle
